import { Alert, Button, Flex, Form, Input } from "antd";
import { useTranslation } from "react-i18next";

import type { AuthProvider } from "shell/api/auth";

import { ProviderCard } from "common-services/features/shared/ProviderCard";
import { Loading } from "design-system/components/Loading/Loading";

import { useProviderDiscovery } from "./hooks/useProviderDiscovery";
import s from "./ProviderDiscovery.module.css";

interface ProviderDiscoveryProps {
  onPasswordLogin: (email: string) => void;
  onProviderSelected: (provider: AuthProvider, email?: string) => void;
}

export const ProviderDiscovery = ({
  onProviderSelected,
  onPasswordLogin,
}: ProviderDiscoveryProps) => {
  const { state, lookupDomain, reset } = useProviderDiscovery();
  const { t } = useTranslation(["common"]);

  const { step } = state;

  if (step === "resolved") {
    const { email, resolution } = state;

    return (
      <ProviderCard
        optionsProps={{
          email,
          resolution,
          onProviderSelected,
          onPasswordLogin,
          onReset: reset,
        }}
      />
    );
  }

  if (step === "error") {
    return (
      <ProviderCard>
        <Flex vertical gap="middle" className={s.fullWidth}>
          <Alert type="warning" title={state.message} showIcon />
          <Button block onClick={reset}>
            {t("common:idpDiscovery.tryAnotherEmail")}
          </Button>
        </Flex>
      </ProviderCard>
    );
  }

  if (step === "loading") {
    return (
      <ProviderCard>
        <Flex justify="center">
          <Loading size="large" />
        </Flex>
      </ProviderCard>
    );
  }

  return (
    <ProviderCard>
      <Form<{ email: string }>
        onFinish={({ email }) => lookupDomain(email)}
        layout="vertical"
      >
        <Form.Item
          name="email"
          label={t("idpDiscovery.emailLabel")}
          rules={[
            { required: true, message: t("labels.emailRequired") },
            { type: "email", message: t("labels.emailInvalid") },
          ]}
        >
          <Input
            type="email"
            placeholder={t("idpDiscovery.emailPlaceholder")}
            size="large"
            autoComplete="username"
          />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" block size="large">
            {t("common:idpDiscovery.continue")}
          </Button>
        </Form.Item>
      </Form>
    </ProviderCard>
  );
};
